import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { FormInput, FormSelect } from '../../shared/components/Fieldset'
import TableView from '../../shared/components/TableView'
import { API_URL } from '../../constants'
import { Loading, LoadingOverlay } from '../../shared/components/Loading'
import { languageUtil } from '../../util/languageUtils'
import { useTranslation } from 'react-i18next'

const FilterableTableView = ({
    columns = [],
    data = [],
    itemsPerPage = 10,
    searchKeys = ['title'],
    searchPlaceholder = 'Search...',
    filters = [],
    onRowClick,
    renderRow,
    title,
    apiEndpoint = null,
}) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [filterValues, setFilterValues] = useState({})
    const [apiData, setApiData] = useState(data)
    const [loading, setLoading] = useState(false)
    const [totalItems, setTotalItems] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })

    // Use a ref to prevent immediate API calls on initial render
    const initialRender = useRef(true)
    const debounceTimer = useRef(null)

    const { language } = languageUtil()
    const { t } = useTranslation()

    // Build API query parameters from filter values
    const buildApiParams = useCallback(
        (filters, search, page = 1, sort = null) => {
            const params = new URLSearchParams()

            // Add search query
            if (search) {
                params.append('search', search)
                if (searchKeys.length > 0) {
                    params.append('search_fields', searchKeys.join(','))
                }
            }

            // Add filter values
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== '' && value !== null && value !== undefined) {
                    let paramKey = key

                    // Rename filter keys for API compatibility
                    if (key.includes('start_date')) {
                        paramKey = 'date_from'
                    } else if (key.includes('end_date')) {
                        paramKey = 'date_to'
                    } else if (key.includes('min_amount')) {
                        paramKey = 'amount_min'
                    } else if (key.includes('max_amount')) {
                        paramKey = 'amount_max'
                    }
                    params.append(paramKey, value)
                }
            })

            // Add pagination
            // params.append('page', page)
            // params.append('per_page', itemsPerPage)

            // Add sorting
            if (sort && sort.key) {
                params.append('sort_by', sort.key)
                params.append('sort_order', sort.direction)
            }

            return params
        },
        [searchKeys, itemsPerPage]
    )

    // Enhanced fetchData with error handling and retry logic
    const fetchDataWithRetry = useCallback(
        async (params, retries = 3) => {
            if (!apiEndpoint) return

            setLoading(true)

            for (let attempt = 1; attempt <= retries; attempt++) {
                try {
                    const token = localStorage.getItem('token')

                    const url = `${API_URL}/admin${apiEndpoint}?${params.toString()}`
                    const response = await fetch(url, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Accept: 'application/json',
                            Authorization: `Bearer ${token}`,
                            'Accept-Language': language,
                        },
                    })

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`)
                    }

                    const result = await response.json()

                    // Adjust based on your API response structure
                    setApiData(result.data || result)
                    setTotalItems(result.total || result.length || 0)
                    setLoading(false)
                    return // Success, exit the function
                } catch (error) {
                    console.error(`Attempt ${attempt} failed:`, error)

                    if (attempt === retries) {
                        // All retries failed, use fallback data
                        console.error('All retry attempts failed, using fallback data')
                        setApiData(data)
                        setTotalItems(data.length)
                        setLoading(false)
                    } else {
                        // Wait before retrying (exponential backoff)
                        await new Promise(resolve => setTimeout(resolve, 1000 * attempt))
                    }
                }
            }
        },
        [apiEndpoint, data]
    )

    // Handle filter changes with debouncing
    const handleFilterChange = (key, value) => {
        setFilterValues(prev => ({ ...prev, [key]: value }))

        // Clear any existing timer
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }

        // Set new timer for debouncing (500ms delay)
        debounceTimer.current = setTimeout(() => {
            if (apiEndpoint) {
                const params = buildApiParams(
                    { ...filterValues, [key]: value },
                    searchQuery,
                    currentPage,
                    sortConfig
                )
                fetchDataWithRetry(params)
            }
        }, 500)
    }

    // Handle search with debouncing
    const handleSearchChange = value => {
        setSearchQuery(value)

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }

        debounceTimer.current = setTimeout(() => {
            if (apiEndpoint) {
                const params = buildApiParams(filterValues, value, currentPage, sortConfig)
                fetchDataWithRetry(params)
            }
        }, 500)
    }

    // Handle page change
    const handlePageChange = page => {
        setCurrentPage(page)
        if (apiEndpoint) {
            const params = buildApiParams(filterValues, searchQuery, page, sortConfig)
            fetchDataWithRetry(params)
        }
    }

    // Handle sort
    const handleSort = key => {
        const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
        const newSortConfig = { key, direction }
        setSortConfig(newSortConfig)

        if (apiEndpoint) {
            const params = buildApiParams(filterValues, searchQuery, currentPage, newSortConfig)
            fetchDataWithRetry(params)
        }
    }

    // Reset all filters
    const handleResetFilters = () => {
        setSearchQuery('')
        setFilterValues({})
        setCurrentPage(1)
        setSortConfig({ key: null, direction: 'asc' })

        if (apiEndpoint) {
            const params = buildApiParams({}, '', 1, null)
            fetchDataWithRetry(params)
        }
    }

    // Filter data locally if no API endpoint is provided
    const filteredData = useMemo(() => {
        if (apiEndpoint) {
            if (loading || apiData.length === 0) {
                return data
            }
            return apiData
        }

        let filtered = data

        // Search
        if (searchQuery) {
            filtered = filtered.filter(row =>
                searchKeys.some(key =>
                    String(row[key] || '')
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase())
                )
            )
        }

        // Filters
        for (const filter of filters) {
            const { key, type, startKey, endKey, minKey, maxKey } = filter

            if (type === 'date-range') {
                const start = filterValues[startKey]
                const end = filterValues[endKey]

                if (start) {
                    filtered = filtered.filter(row => new Date(row[key]) >= new Date(start))
                }
                if (end) {
                    filtered = filtered.filter(row => new Date(row[key]) <= new Date(end))
                }
            } else if (type === 'range') {
                const min = filterValues[minKey]
                const max = filterValues[maxKey]

                if (min) {
                    filtered = filtered.filter(row => parseFloat(row[key]) >= parseFloat(min))
                }
                if (max) {
                    filtered = filtered.filter(row => parseFloat(row[key]) <= parseFloat(max))
                }
            } else {
                const val = filterValues[key]
                if (!val || val === 'all') continue

                filtered = filtered.filter(row => String(row[key]) === val)
            }
        }

        return filtered
    }, [apiEndpoint, data, searchQuery, searchKeys, filterValues, filters, apiData])

    // Initial data fetch
    useEffect(() => {
        if (apiEndpoint && !initialRender.current) {
            const params = buildApiParams(filterValues, searchQuery, currentPage, sortConfig)
            fetchDataWithRetry(params)
        }
        initialRender.current = false
    }, [])

    // Clean up timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }
        }
    }, [])

    return (
        <div>
            <div className='flex items-center justify-between mb-6'>
                <h2 className='text-2xl font-bold text-gray-800'>{title}</h2>
                {apiEndpoint && (
                    <button
                        onClick={handleResetFilters}
                        className='px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-gray-100 rounded-md hover:bg-gray-200'
                        disabled={loading}
                    >
                        {t('donations_table.filters.status.reset')}
                    </button>
                )}
            </div>

            {/* Loading indicator */}
            {loading && <LoadingOverlay />}

            {/* Top Bar */}
            <div className='flex flex-col gap-4 p-4 mb-6 rounded-lg bg-gray-50'>
                {/* Row 1: Select + Search */}
                <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                    {filters
                        .filter(filter => filter.type === 'select')
                        .map(filter => (
                            <FormSelect
                                key={filter.key}
                                label={filter.label}
                                value={filterValues[filter.key] || ''}
                                onChange={e => handleFilterChange(filter.key, e.target.value)}
                                options={filter.options}
                                disabled={loading}
                            />
                        ))}

                    <FormInput
                        placeholder={searchPlaceholder}
                        value={searchQuery}
                        onChange={e => handleSearchChange(e.target.value)}
                        label={t('search.default')}
                        disabled={loading}
                        icon={
                            <svg
                                className='w-5 h-5 text-gray-400'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                                ></path>
                            </svg>
                        }
                    />
                </div>

                {/* Row 2: Date + Range */}
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4'>
                    {filters
                        .filter(filter => filter.type === 'date-range')
                        .flatMap(filter => [
                            <FormInput
                                key={`${filter.key}-start`}
                                label={`${filter.label} ${t('donations_table.filters.status.from')}`}
                                type='date'
                                value={filterValues[filter.startKey] || ''}
                                onChange={e => handleFilterChange(filter.startKey, e.target.value)}
                                disabled={loading}
                            />,
                            <FormInput
                                key={`${filter.key}-end`}
                                label={`${filter.label} ${t('donations_table.filters.status.to')}`}
                                type='date'
                                value={filterValues[filter.endKey] || ''}
                                onChange={e => handleFilterChange(filter.endKey, e.target.value)}
                                disabled={loading}
                            />,
                        ])}

                    {filters
                        .filter(filter => filter.type === 'range')
                        .flatMap(filter => [
                            <FormInput
                                key={`${filter.key}-min`}
                                label={`${filter.label} ${t('donations_table.filters.status.min')}`}
                                type='number'
                                value={filterValues[filter.minKey] || ''}
                                onChange={e => handleFilterChange(filter.minKey, e.target.value)}
                                disabled={loading}
                            />,
                            <FormInput
                                key={`${filter.key}-max`}
                                label={`${filter.label} ${t('donations_table.filters.status.max')}`}
                                type='number'
                                value={filterValues[filter.maxKey] || ''}
                                onChange={e => handleFilterChange(filter.maxKey, e.target.value)}
                                disabled={loading}
                            />,
                        ])}
                </div>
            </div>

            {/* Table */}
            <TableView
                columns={columns.map(col => ({
                    ...col,
                    onSort: col.sortable ? () => handleSort(col.key) : undefined,
                }))}
                data={filteredData}
                itemsPerPage={itemsPerPage}
                search={false} // Turn off internal search
                onRowClick={onRowClick}
                renderRow={renderRow}
                loading={loading}
                pagination={
                    apiEndpoint
                        ? {
                              currentPage,
                              totalItems,
                              itemsPerPage,
                              onPageChange: handlePageChange,
                          }
                        : undefined
                }
                sortConfig={sortConfig}
            />
        </div>
    )
}

export default FilterableTableView
